/*
 * <bh-phone-field> — makes the cart's phone number actually required.
 *
 * Shopify's checkout cannot enforce this on our plan (see snippets/bh-phone-field.liquid for
 * the full why), so the gate lives here, on the cart form that submits to checkout.
 *
 * Both cart forms carry `novalidate`, which switches OFF native constraint validation — so the
 * input's `required` attribute alone does nothing. Rather than strip `novalidate` (the same
 * forms hold the quantity inputs, and turning validation back on for those could block checkout
 * in ways we have not tested on a live store), this validates just its own field on submit.
 *
 * Degradation: if this script fails to load, the input still posts its value with the form. The
 * number is captured, merely not enforced. A broken gate must never mean a checkout nobody can
 * complete.
 */
/*
 * Where the abandoned-cart beacon goes (see notifyQuickOrder). Hardcoded because the theme has
 * no environment to read: it is a public URL, not a secret, and a theme setting for it would be
 * a setting nobody ever changes.
 */
const QUICKORDER_CART_PHONE_URL = 'https://quickorder-production-1608.up.railway.app/api/cart-phone';

class BhPhoneField extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this.input = this.querySelector('input[type="tel"]');
    this.err = this.querySelector('.bh-phone-field__err');
    if (!this.input) return;
    this._built = true;

    // `input.form`, not closest('form'): on the cart PAGE the checkout button and this field sit
    // OUTSIDE the cart <form> and are wired to it with `form="cart-form"`. HTMLInputElement.form
    // resolves both that attribute and plain nesting; closest() would miss the page case entirely.
    this.form = this.input.form;
    if (this.form) this.form.addEventListener('submit', (e) => this._onSubmit(e));

    // Live feedback while typing. Clearing the error the moment they start fixing it matters —
    // an error that outlives the problem reads as "still broken" and people give up. The green
    // tick is the other half: it confirms the number was accepted without making them submit to
    // find out, which is the single most common question a required field provokes.
    this.input.addEventListener('input', () => {
      if (this._invalid) this._clear();
      this._reflectValidity();
    });
    this._reflectValidity(); // a prefilled value from the cart attribute should show as valid

    // Persist to the cart so the value survives navigating drawer -> cart page, and so an
    // abandoned checkout still leaves us the number. Debounced; failure is non-blocking.
    this.input.addEventListener('blur', () => this._persist());
  }

  /**
   * Normalise a phone number, permissively.
   *
   * This gate is the ONLY thing standing between a customer and checkout (see _onSubmit), so it
   * is deliberately generous. It used to accept Bulgarian mobiles and nothing else, which did
   * not merely store a badly formatted number — it returned null for every foreign number and
   * _onSubmit called preventDefault(), so the checkout button silently did nothing. UK customers
   * buying cakes for people in Sofia could not complete an order at all. That is why this is
   * loose: rejecting a real customer costs an order, accepting an odd number costs nothing,
   * because a human can still read and ring it when an order needs a call.
   *
   * Bulgarian shortcuts still normalise to +359 so the common case stays tidy. Anything else
   * that looks like a real international number is passed through with separators stripped, and
   * the authoritative libphonenumber-js parse happens server-side in QuickOrder's
   * notify-shopify-order task before it reaches Shopify's customer record. The theme has no
   * bundler, so the library cannot run here — and loading it from a CDN in the checkout path
   * would be a worse trade than being permissive.
   *
   * E.164 allows at most 15 digits; 8 is below any real national number, so the range is the
   * only thing actually enforced.
   */
  static normalize(raw) {
    const s = String(raw || '').replace(/[\s\-().\u00A0]/g, '');
    if (!s) return null;

    // Bulgarian mobile, in any of the three shapes customers actually type.
    let m = s.match(/^(?:\+359|00359|359)(8\d{8})$/);
    if (m) return '+359' + m[1];
    m = s.match(/^0(8\d{8})$/);
    if (m) return '+359' + m[1];

    // Explicit international: +44…, 0044… (00 is the international prefix, normalised to +).
    m = s.match(/^(?:\+|00)(\d{8,15})$/);
    if (m) return '+' + m[1];

    return null;
  }

  _onSubmit(e) {
    // Only gate the button that actually leaves for checkout. The same form is used for
    // quantity updates and "remove", which must keep working regardless.
    const submitter = e.submitter;
    if (submitter && submitter.name !== 'checkout') return;

    const normalized = BhPhoneField.normalize(this.input.value);
    if (normalized) {
      this.input.value = normalized; // send a consistent format to the order
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    this._invalid = true;
    this.classList.add('bh-phone-field--error');
    this.input.setAttribute('aria-invalid', 'true');
    if (this.err) {
      this.err.textContent = this.input.value.trim()
        ? (window.BhPhoneStrings?.invalid || 'Невалиден телефонен номер.')
        : (window.BhPhoneStrings?.missing || 'Моля, въведи телефонен номер.');
      this.err.hidden = false;
    }
    // Scroll, THEN focus: an instant focus() jump cancels the smooth scroll on phones. The field
    // shakes and pulses the same way an overdue cake warning does (assets/bh-cart-groups.css),
    // so "you missed something" looks the same everywhere in the cart. Both checkout paths land
    // here — the real button, and the sticky bar, which clicks the real button.
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const box = this.querySelector('.bh-phone-field__wrap') || this;
    box.classList.remove('bh-attention');
    void box.offsetWidth; // restart on a repeat tap
    box.classList.add('bh-attention');
    setTimeout(() => this.input.focus({ preventScroll: true }), 420);
  }

  _clear() {
    this._invalid = false;
    this.classList.remove('bh-phone-field--error');
    this.input.removeAttribute('aria-invalid');
    if (this.err) this.err.hidden = true;
  }

  /** Green tick as soon as the number is usable. Class only — the tick itself is CSS. */
  _reflectValidity() {
    this.classList.toggle('bh-phone-field--valid', !!BhPhoneField.normalize(this.input.value));
  }

  _persist() {
    const normalized = BhPhoneField.normalize(this.input.value);
    if (!normalized || normalized === this._saved) return;
    this._saved = normalized;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Телефон': normalized } })
    })
      // The response IS the updated cart, so the value and size of what they were about to buy
      // come free here. Worth carrying: a €69 cake is worth a phone call and a €3 add-on is not.
      .then((r) => r.json())
      .then((cart) => {
        BhPhoneField.notifyQuickOrder(normalized, cart);
        if (window.bhClarityEvent) window.bhClarityEvent('bh_phone_entered'); // funnel step, bh-clarity-events.js
      })
      .catch(() => { /* bookkeeping only — never block the customer on it */ });
  }

  /**
   * Tell QuickOrder that a real number just landed in the cart, so that if no order follows
   * within ~20 minutes the team gets a Telegram card and can ring a lead that is still warm.
   *
   * Shopify's own abandoned-checkout tooling cannot do this job. Shopify only begins keeping an
   * abandoned checkout once contact details are entered AT CHECKOUT, and this field sits one
   * step earlier, in the cart, because checkout cannot require a phone on this plan (the long
   * version is in snippets/bh-phone-field.liquid). On 2026-09-12 the store had three
   * abandoned-checkout records in its entire history while taking ~6.5 orders a day, so that
   * dataset is not thin here, it is blind. This beacon is the only signal that exists.
   *
   * sendBeacon rather than fetch: blur is very often the last thing that happens on the page —
   * they tab away, they close it — and a beacon is queued by the browser and survives unload,
   * where an in-flight fetch is cancelled. Losing the request on exactly the people who leave
   * would defeat the entire point. A text/plain Blob also keeps it a CORS simple request, so
   * there is no preflight to fail.
   *
   * Nothing reads the response and nothing here can throw. A customer's checkout must never
   * depend on the back office being reachable.
   */
  /** Customer-visible keys only: "_"-prefixed ones are internal plumbing (ids, tiers, flags). */
  static visible(obj) {
    const out = {};
    if (!obj) return out;
    Object.keys(obj).forEach((k) => {
      if (k.charAt(0) !== '_' && obj[k] != null && obj[k] !== '') out[k] = String(obj[k]);
    });
    return out;
  }

  /** The cart lines in the shape QuickOrder's /api/cart-phone expects (see its route). */
  static cartLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.slice(0, 30).map((i) => {
      const p = i.properties || {};
      return {
        title: i.product_title || i.title,
        variant: i.variant_title || null,
        handle: i.handle || null,
        quantity: i.quantity,
        price: i.final_line_price / 100,
        type: p._Type || null,
        cakeId: p._Cake_ID || null,
        relatedTo: p._Related_To || null,
        properties: BhPhoneField.visible(p)
      };
    });
  }

  static notifyQuickOrder(phone, cart) {
    if (!navigator.sendBeacon) return;
    try {
      const body = JSON.stringify({
        phone: phone,
        cartToken: cart && cart.token,
        // Clarity visitor id, so the alert can open every recording from this browser. Set on the
        // cart by snippets/bh-clarity-tags.liquid; the _clck cookie ("<id>^…") covers a first
        // add that beat it. Absent when the visitor declined cookies (Clarity does not record).
        clarityId: (cart && cart.attributes && cart.attributes._clarity_id) ||
          ((document.cookie.match(/(?:^|; )_clck=([^;%^|]+)/) || [])[1] || null),
        // Shopify reports money in minor units; QuickOrder stores and prints whole EUR.
        total: cart && typeof cart.total_price === 'number' ? cart.total_price / 100 : null,
        // Products only. Add-ons, rush and delivery fees are their own lines tagged `_Type` (see
        // bh-cart-integrity.js), so cart.item_count would read one cake with two add-ons as 3.
        itemCount: cart && Array.isArray(cart.items)
          ? cart.items.reduce((n, i) => n + (i.properties && i.properties._Type ? 0 : i.quantity), 0)
          : null,
        // What they were about to buy, so the alert can print it like an order card and the team
        // can follow up knowing the cake. Snapshot at the moment the phone landed.
        items: BhPhoneField.cartLines(cart),
        attributes: BhPhoneField.visible(cart && cart.attributes)
      });
      navigator.sendBeacon(QUICKORDER_CART_PHONE_URL, new Blob([body], { type: 'text/plain' }));
    } catch (e) { /* bookkeeping only — never block the customer on it */ }
  }
}

if (!customElements.get('bh-phone-field')) customElements.define('bh-phone-field', BhPhoneField);
